import { TDatabaseId } from '@infotech-soe/metaschema';
import { ConnectedProps, legacy_connect as connect } from 'react-redux';

import { ConfirmationType } from '../../../models/confirmation/ConfirmationEntity';
import { ConfirmationMessageType } from '../../../services/commands/UserInteractor';
import UniqueId from '../../../services/uid/UniqueId';
import { AppState } from '../../../store/reducers/root';
import { getCatalogsStateEntity } from '../../../store/selectors/catalogs';
import catalogs from '../../../store/slices/catalogs';
import confirmation from '../../../store/slices/confirmation';

import CatalogsTree from './CatalogsTree';
import { findNode } from './utils';

interface Props {
  treeId: UniqueId;
  title?: string;
  onSelect?: (id: TDatabaseId) => void;
}

const mapStateToProps = (state: AppState, ownProps: Props) => ({
  tree: getCatalogsStateEntity(state, ownProps.treeId),
});

const mapDispatchToProps = {
  expandNode: catalogs.expandNode,
  collapseNode: catalogs.collapseNode,
  selectNode: catalogs.selectNode,
  toggleCheck: catalogs.toggleCheck,
  setHierarchy: catalogs.setHierarchy,
  nextNode: catalogs.nextNode,
  prevNode: catalogs.prevNode,
  refresh: catalogs.refresh,
  clearChecks: catalogs.clearChecks,
  moveIn: catalogs.moveIn,
  showConfirmation: confirmation.show,
};

const mergeProps = (
  sP: ReturnType<typeof mapStateToProps>,
  {
    expandNode,
    collapseNode,
    selectNode,
    toggleCheck,
    setHierarchy,
    nextNode,
    prevNode,
    refresh,
    clearChecks,
    moveIn,
    showConfirmation,
  }: typeof mapDispatchToProps,
  { treeId: catalogId, onSelect: onSelectProp, ...oP }: Props
) => ({
  ...sP,
  ...oP,
  onExpand: (nodeId?: TDatabaseId) => expandNode({ catalogId, nodeId }),
  onCollapse: (nodeId?: TDatabaseId) => collapseNode({ catalogId, nodeId }),
  onSelect: (nodeId: TDatabaseId) => {
    onSelectProp?.(nodeId);
    selectNode({ catalogId, nodeId });
  },
  onCheckToggle: (nodeId?: TDatabaseId, shift?: boolean) =>
    toggleCheck({ catalogId, nodeId, shift }),
  onSetHierarchy: (nodeId?: TDatabaseId) => setHierarchy({ catalogId, nodeId }),
  onNext: () => nextNode({ catalogId }),
  onPrev: () => prevNode({ catalogId }),
  onRefresh: () => refresh({ catalogId }),
  onClearChecked: () => clearChecks({ catalogId }),
  onMoveIn: (moveIds: TDatabaseId[], newParentId: TDatabaseId) =>
    moveIn({ catalogId, moveIds, newParentId }),
  onShowConfirmation: (
    command: string,
    nodeId?: TDatabaseId | TDatabaseId[],
    parentId?: TDatabaseId
  ) => {
    showConfirmation({
      type: ConfirmationType.CATALOGS,
      action: {
        command,
        args: {
          treeId: sP.tree.categoryId,
          nodeId: nodeId ?? sP.tree.activeId,
          name:
            command === ConfirmationMessageType.RENAME_CATALOG &&
            typeof nodeId !== 'object'
              ? findNode(sP.tree.nodes, nodeId ?? sP.tree.activeId)?.name
              : undefined,
          parentId,
        },
        hook: false,
      },
      messageType: command as ConfirmationMessageType,
    });
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

export type CatalogsTreeConnectorProps = ConnectedProps<typeof connector>;

export default connector(CatalogsTree);
